import type { SidecarFileRequest } from '../../api';
import { ALLOWED_IMAGE_TYPES, validateFiles, type AcceptedFile } from './constants';

/**
 * Upload with JSON: pair each image with the `.json` of the same name.
 *
 * `puma-beach.png` pairs with `puma-beach.json` — the name up to the last dot,
 * compared exactly, case and all. Pairing runs over the whole selection, not
 * per drop, so the images and their JSON can arrive in separate drops.
 *
 * Everything that can be told without the server is told here: a JSON with no
 * image, an image with no JSON, two files competing for one name, a JSON that
 * does not parse. What the JSON *says* is the backend's to judge
 * (`metadata_from_sidecar`), so the vocabulary lives in one place.
 */

/** A JSON larger than this is not a metadata file; refused unread. */
export const MAX_SIDECAR_BYTES = 64 * 1024;

export interface DroppedFile {
  key: string;
  file: File;
}

/** An image the server is asked about, with the JSON paired with it. */
export interface SidecarFile extends AcceptedFile {
  key: string;
  /** The key of the paired JSON, so removing the image removes both. */
  jsonKey: string;
  jsonName: string;
  sidecar: Record<string, unknown>;
}

export interface PairingRejection {
  key: string;
  filename: string;
  reason: string;
  /** The key of the JSON paired with a rejected image, so removing the image removes both. */
  jsonKey?: string;
  /** Refused only for the per-upload file cap; not counted toward the rejection limit. */
  overLimit?: boolean;
}

export interface Pairing {
  images: SidecarFile[];
  rejected: PairingRejection[];
}

/** What a dropped JSON file turned out to hold. */
export type ParsedSidecar =
  { ok: true; value: Record<string, unknown> } | { ok: false; reason: string };

const isJson = (file: File) => file.name.toLowerCase().endsWith('.json');

/** The name up to the last dot: what an image and its JSON share. */
export function stemOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/**
 * Hidden files a folder drop brings along (`.DS_Store`, `._puma.png`).
 * Skipped rather than rejected: nobody chose them, and listing them would
 * count toward the rejection limit.
 */
const isHidden = (file: File) => file.name.startsWith('.');

/** Read once per file: the selection is re-paired on every change. */
const parsedCache = new WeakMap<File, Promise<ParsedSidecar>>();

async function parseSidecar(file: File): Promise<ParsedSidecar> {
  if (file.size > MAX_SIDECAR_BYTES) {
    return { ok: false, reason: `${file.name} is too large to be a metadata file` };
  }
  let text: string;
  try {
    text = await file.text();
  } catch {
    return { ok: false, reason: `${file.name} could not be read` };
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, reason: `${file.name} is not valid JSON` };
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ok: false, reason: `${file.name} is not a JSON object` };
  }
  return { ok: true, value: value as Record<string, unknown> };
}

/** Parse every JSON in the selection, keyed by file. */
export async function readSidecars(files: File[]): Promise<Map<File, ParsedSidecar>> {
  const jsonFiles = files.filter((file) => isJson(file) && !isHidden(file));
  const parsed = await Promise.all(
    jsonFiles.map((file) => {
      let pending = parsedCache.get(file);
      if (!pending) {
        pending = parseSidecar(file);
        parsedCache.set(file, pending);
      }
      return pending;
    }),
  );
  return new Map(jsonFiles.map((file, index) => [file, parsed[index]]));
}

/**
 * Pair the selection's images with their JSON.
 *
 * `parsed` is `readSidecars` of the same files; kept separate so this stays a
 * pure function of its input. Images go through the same `validateFiles` as
 * every other upload (type, size, the 500-file cap); JSON files do not count
 * toward the cap, so a full drop is up to 500 images and 500 JSON files.
 */
export function pairSidecars(dropped: DroppedFile[], parsed: Map<File, ParsedSidecar>): Pairing {
  const rejected: PairingRejection[] = [];
  const visible = dropped.filter(({ file }) => !isHidden(file));
  const jsons = visible.filter(({ file }) => isJson(file));
  const others = visible.filter(({ file }) => !isJson(file));

  // A name two files of the same side compete for pairs with neither: which
  // image a JSON describes would be a guess.
  const count = (files: DroppedFile[]) => {
    const counts = new Map<string, number>();
    for (const { file } of files) {
      const stem = stemOf(file.name);
      counts.set(stem, (counts.get(stem) ?? 0) + 1);
    }
    return counts;
  };
  const jsonCounts = count(jsons);
  const otherCounts = count(others);
  const isDuplicate = ({ file }: DroppedFile) => {
    const stem = stemOf(file.name);
    return (isJson(file) ? jsonCounts : otherCounts).get(stem)! > 1;
  };
  const jsonByStem = new Map(
    jsons.filter((entry) => !isDuplicate(entry)).map((entry) => [stemOf(entry.file.name), entry]),
  );
  // A refused image takes its JSON with it when removed: left behind, the JSON
  // would come back as "no matching image" and count toward the limit again.
  const jsonKeyFor = ({ file }: DroppedFile) => jsonByStem.get(stemOf(file.name))?.key;

  for (const entry of visible.filter(isDuplicate)) {
    rejected.push({
      key: entry.key,
      filename: entry.file.name,
      reason: `more than one ${isJson(entry.file) ? 'JSON file' : 'file'} is named ${stemOf(entry.file.name)}`,
      jsonKey: isJson(entry.file) ? undefined : jsonKeyFor(entry),
    });
  }

  const candidates = others.filter((entry) => !isDuplicate(entry));
  const keyByFile = new Map(candidates.map((entry) => [entry.file, entry.key]));
  const validated = validateFiles(
    candidates.map(({ file }) => file),
    0,
    ALLOWED_IMAGE_TYPES,
  );
  for (const refused of validated.rejected) {
    const entry = candidates.find(({ file }) => file.name === refused.filename)!;
    rejected.push({ key: entry.key, ...refused, jsonKey: jsonKeyFor(entry) });
  }

  const images: SidecarFile[] = [];
  for (const accepted of validated.accepted) {
    const key = keyByFile.get(accepted.file)!;
    const json = jsonByStem.get(stemOf(accepted.file.name));
    if (!json) {
      rejected.push({ key, filename: accepted.file.name, reason: 'no matching JSON file' });
      continue;
    }
    const result = parsed.get(json.file);
    if (!result || !result.ok) {
      rejected.push({
        key,
        filename: accepted.file.name,
        reason: result?.reason ?? `${json.file.name} could not be read`,
        jsonKey: json.key,
      });
      continue;
    }
    images.push({
      ...accepted,
      key,
      jsonKey: json.key,
      jsonName: json.file.name,
      sidecar: result.value,
    });
  }

  // A JSON is answered for by its image whenever one carries its name, even
  // a refused one: the image's row already says what is wrong.
  const imageStems = new Set(others.map(({ file }) => stemOf(file.name)));
  for (const [stem, json] of jsonByStem) {
    if (!imageStems.has(stem)) {
      rejected.push({ key: json.key, filename: json.file.name, reason: 'no matching image' });
    }
  }

  return { images, rejected };
}

/** The `files` payload for `POST /sidecar-uploads`, in the same order as `images`. */
export function toSidecarRequests(images: SidecarFile[]): SidecarFileRequest[] {
  return images.map(({ file, content_type, sidecar }) => ({
    filename: file.name,
    content_type,
    sidecar,
  }));
}
